import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useNavigate } from "../app/navigation.js";
import { ArrowLeft, Volume2, VolumeX, Smile, ChevronRight, Lightbulb, SkipForward, Send } from "lucide-react";
import {
  canBeat,
  cardLabel,
  comboLabel,
  isLegalReturnTributeCard,
  legalPlays,
  rankLabel,
  rankOf,
  rulesetById,
  type Card,
  type Combo,
  type ComboType,
  type Seat,
} from "@guandan/engine";
import {
  isOnTurn, lineById, type Presentation,
  type HintReason,
  type HintSuggestion,
  type MatchSummary,
  type PlayerMatchStats,
  type ReactionEvent,
  type TableAction,
} from "@guandan/protocol";
import { audio } from "../audio/AudioBus.js";
import { bombSound } from "../audio/sfx.js";
import { Avatar, type AvatarExpression } from "../art/Avatar.js";
import { CardFace } from "../art/Card.js";
import { reactionDefinition, TABLE_REACTIONS } from "../art/reactionManifest.js";
import { Effects } from "../game/effects.js";
import { fanCardAtPoint, fanPlacement, mobileLiveLayout, wideLiveLayout, type TableLayout, type WideLiveLayout } from "../game/layout.js";
import { SeatRing } from "../game/SeatRing.js";
import { useSize } from "../game/useSize.js";
import { useGameSession } from "../game/useGameSession.js";
import type { Cue } from "../game/scheduler.js";
import { refreshSession } from "../net/session.js";
import { api } from "../net/api.js";
import { Button, Spinner } from "../ui/primitives.js";
import "./table.css";
import { HandRewards, HandRewardHistory } from "./HandRewards.js";
import { useLocale } from "../i18n.js";
import { isWidePath } from "../app/widePath.js";
import { useUiPreferences } from "../app/UiPreferences.js";
import { groupTableActionsByTrick, rememberCompletedHand, type CompletedHandView } from "../game/tableHistory.js";
import { WideHistoryPanel, WidePlayerPanel } from "./WideTablePanels.js";
import { selectionOptions, type SelectionOption } from "../game/selectionOptions.js";

const REACTION_POSITION_KEY = "guandan:reaction-position";
const REACTION_FAB_SIZE = 56;

/*
 * The draggable reaction button is `position: fixed`, so its coordinates are
 * viewport coordinates — but it belongs to the phone frame, which on anything
 * wider than a phone is a centred column rather than the whole window. Clamping
 * against the window would park the button out on the empty page beside it, so
 * every bound below comes from the frame's own rect.
 */
function frameRect() {
  return document.querySelector(".table-wide .table-surface")?.getBoundingClientRect()
    ?? document.querySelector(".app-frame")?.getBoundingClientRect()
    ?? new DOMRect(0, 0, window.innerWidth, window.innerHeight);
}

function clampReactionPosition(position: { x: number; y: number }) {
  const frame = frameRect();
  return {
    x: Math.max(frame.left + 10, Math.min(frame.right - REACTION_FAB_SIZE - 10, position.x)),
    y: Math.max(frame.top + 64, Math.min(frame.bottom - REACTION_FAB_SIZE - 82, position.y)),
  };
}

function initialReactionPosition() {
  const frame = frameRect();
  const fallback = { x: frame.right - REACTION_FAB_SIZE - 14, y: frame.top + 72 };
  try {
    const saved = JSON.parse(window.localStorage.getItem(REACTION_POSITION_KEY) ?? "null") as { x?: unknown; y?: unknown } | null;
    if (typeof saved?.x === "number" && typeof saved.y === "number") return clampReactionPosition({ x: saved.x, y: saved.y });
  } catch { /* A malformed preference should never block the table. */ }
  return clampReactionPosition(fallback);
}

export function Table({ wide = false }: { wide?: boolean }) {
  const { t } = useLocale();
  const { preferences } = useUiPreferences();
  const { roomId = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const wideTable = wide || isWidePath(location.pathname);

  const effects = useMemo(() => new Effects(), []);
  const shakeRef = useRef<HTMLDivElement | null>(null);

  /**
   * Attach the effects canvas the moment it exists.
   *
   * A callback ref, for the same reason the surface uses one: the table renders
   * a loading state first, so an effect keyed on a ref object would capture
   * null and never run again — leaving the canvas at its default 300x150 and
   * every particle drawn off screen.
   */
  const fxRef = useCallback(
    (node: HTMLCanvasElement | null) => {
      effects.attach(node, (x, y) => {
        const shake = shakeRef.current;
        if (shake !== null)
          shake.style.transform =
            x === 0 && y === 0 ? "" : `translate(${x}px, ${y}px)`;
      });
    },
    [effects],
  );
  // Seat geometry, kept in a ref so the cue handler can read the current layout
  // without being torn down and rebuilt every time the table resizes.
  const geometryRef = useRef<{
    seats: { x: number; y: number; playX: number; playY: number }[];
    cx: number;
    cy: number;
    w: number;
  }>({
    seats: [],
    cx: 0,
    cy: 0,
    w: 0,
  });
  const [avatarReactions, setAvatarReactions] = useState<
    Partial<Record<Seat, AvatarExpression>>
  >({});
  const [drama, setDrama] = useState<(Presentation & { id: number }) | null>(null);
  const [animatedPlaySeq, setAnimatedPlaySeq] = useState<number | null>(null);
  const [speechBubble, setSpeechBubble] = useState<{ seat: Seat; text: string } | null>(null);
  const viewerRef = useRef<Seat | null>(null);
  const dramaTimer = useRef<number | null>(null);
  const speechTimer = useRef<number | null>(null);
  const landingTimer = useRef<number | null>(null);
  const clearVisual = useCallback(() => {
    for (const ref of [dramaTimer, landingTimer]) {
      if (ref.current !== null) window.clearTimeout(ref.current);
      ref.current = null;
    }
    effects.clear(); setDrama(null); setAnimatedPlaySeq(null);
  }, [effects]);
  const resetPresentation = useCallback(() => {
    for (const ref of [dramaTimer, speechTimer, landingTimer]) {
      if (ref.current !== null) window.clearTimeout(ref.current);
      ref.current = null;
    }
    effects.clear(); audio.stopSpeech(); setDrama(null); setAnimatedPlaySeq(null); setSpeechBubble(null);
    Object.values(expressionTimers.current).forEach(timer => window.clearTimeout(timer));
    expressionTimers.current = {}; setAvatarReactions({});
  }, [effects]);
  const expressionTimers = useRef<Partial<Record<Seat, number>>>({});
  const reactionTimers = useRef<Record<string, number>>({});
  const [tableReactions, setTableReactions] = useState<readonly ReactionEvent[]>([]);
  const reactAtSeat = useCallback(
    (seat: Seat, expression: AvatarExpression, hold = 900) => {
      const prior = expressionTimers.current[seat];
      if (prior !== undefined) window.clearTimeout(prior);
      setAvatarReactions((current) => ({ ...current, [seat]: expression }));
      expressionTimers.current[seat] = window.setTimeout(() => {
        setAvatarReactions((current) => {
          const next = { ...current };
          delete next[seat];
          return next;
        });
        delete expressionTimers.current[seat];
      }, hold);
    },
    [],
  );
  const showTableReaction = useCallback((reaction: ReactionEvent) => {
    setTableReactions((current) => {
      const next = [...current, reaction];
      return next.length > 12 ? next.slice(-12) : next;
    });
    const prior = reactionTimers.current[reaction.id];
    if (prior !== undefined) window.clearTimeout(prior);
    reactionTimers.current[reaction.id] = window.setTimeout(() => {
      setTableReactions((current) =>
        current.filter((candidate) => candidate.id !== reaction.id),
      );
      delete reactionTimers.current[reaction.id];
    }, 1_200);
  }, []);
  useEffect(
    () => () => {
      Object.values(expressionTimers.current).forEach((timer) =>
        window.clearTimeout(timer),
      );
      Object.values(reactionTimers.current).forEach((timer) =>
        window.clearTimeout(timer),
      );
      if (dramaTimer.current !== null) window.clearTimeout(dramaTimer.current);
    },
    [],
  );

  /**
   * Sound, particles, and short avatar reactions for one presented event.
   *
   * Driven by the scheduler rather than by React, so effects land on the frame
   * the player sees the event — and, just as importantly, stay silent while the
   * scheduler is fast-forwarding through history nobody watched.
   */
  const onCue = useCallback(
    (cue: Cue) => {
      const geo = geometryRef.current;
      switch (cue.kind) {
        case "reset": resetPresentation(); return;
        case "clearPresentation": clearVisual(); return;
        case "presentation": {
          const p = cue.presentation;
          if (p.durationMs <= 0) return;
          if (dramaTimer.current !== null) window.clearTimeout(dramaTimer.current);
          if (landingTimer.current !== null) window.clearTimeout(landingTimer.current);
          effects.clear();
          setDrama({ ...p, id: Date.now() });
          const box = p.seat === undefined ? undefined : geo.seats[p.seat];
          const won = viewerRef.current !== null && viewerRef.current % 2 === p.winner;
          if (p.scene === "matchEnd") {
            audio.play(won ? "victory" : "defeat");

          } else if (p.scene === "firstOut") audio.play("goOut");
          else if (p.scene === "jiefeng") audio.play("slide");
          else if (p.scene !== "handEnd" && box !== undefined && p.durationMs > 200) {
            landingTimer.current = window.setTimeout(() => {
              landingTimer.current = null;
              if (p.scene === "straightFlush") audio.play("slide");
              else {
                audio.play(bombSound(p.scene === "jokerBomb" ? 1000 : p.scene === "counter" ? 60 : 40));
                effects.bomb(box.playX, box.playY, p.scene === "jokerBomb" ? 1000 : 40,
                  p.intensity === "climax" ? p.scene === "jokerBomb" ? 5 : 3 : 0, p.durationMs - 200);
              }
            }, 200);
          }
          dramaTimer.current = window.setTimeout(() => { effects.clear(); setDrama(null); dramaTimer.current = null; }, p.durationMs);
          return;
        }
        case "deal": audio.play("deal"); return;
        case "play":
          setAnimatedPlaySeq(cue.seq);
          reactAtSeat(cue.seat, "play");
          if (!cue.bomb) {
            if (landingTimer.current !== null) window.clearTimeout(landingTimer.current);
            landingTimer.current = window.setTimeout(() => { audio.play("place"); landingTimer.current = null; }, 200);
          }
          return;
        case "pass": setAnimatedPlaySeq(cue.seq); reactAtSeat(cue.seat, "pass"); audio.play("pass"); return;
        case "trick": audio.play("trick"); return;
        case "out": reactAtSeat(cue.seat, "out", 500); return;
        case "handEnd": case "matchEnd": return;
        case "speech": {
          const line = lineById(cue.cue.lineId);
          if (line === undefined || speechTimer.current !== null) return;
          audio.playSpeech(cue.cue.assetId);
          setSpeechBubble({ seat: cue.cue.seat, text: line.text });
          speechTimer.current = window.setTimeout(() => { setSpeechBubble(null); speechTimer.current = null; }, Math.min(1600, cue.cue.durationBudgetMs));
          return;
        }
      }
    }, [effects, reactAtSeat, resetPresentation, clearVisual],
  );

  const session = useGameSession(roomId, onCue, (cue) => {
    void audio.preloadSpeech(cue.assetId);
  }, showTableReaction);
  const { presented: model, room, canAct, actionState, clearHint } = session;
  viewerRef.current = model.seat;
  useEffect(() => resetPresentation, [resetPresentation]);
  // Never reveal a result before the final play has reached the presentation timeline.
  const visibleHandRewards = session.handSettlements.filter((hand) =>
    hand.handNumber < model.handNumber || (hand.handNumber === model.handNumber &&
      (model.phase === "handOver" || model.phase === "matchOver")));
  const latestHandReward = visibleHandRewards.at(-1);
  useEffect(() => {
    if (latestHandReward) void refreshSession();
  }, [latestHandReward?.handNumber, roomId]);
  const [completedHands, setCompletedHands] = useState<CompletedHandView[]>([]);
  const recordedHand = useRef<number | null>(null);
  useEffect(() => {
    setCompletedHands([]);
    recordedHand.current = null;
  }, [roomId]);
  useEffect(() => {
    if (model.phase !== "handOver" || model.lastResult === null) return;
    if (recordedHand.current === model.handNumber) return;
    recordedHand.current = model.handNumber;
    setCompletedHands((history) => rememberCompletedHand(
      history,
      model.handNumber,
      model.level,
      model.lastResult!,
    ));
  }, [model.phase, model.handNumber, model.lastResult, model.level]);
  useEffect(() => {
    if (model.phase !== "matchOver" || model.summary === null) return;
    const id = window.setTimeout(
      () => navigate(`/result/${roomId}`, { replace: true }),
      1400,
    );
    return () => window.clearTimeout(id);
  }, [model.phase, model.summary, navigate, roomId]);

  const avatarExpression = (seat: Seat): AvatarExpression => {
    if (model.phase === "matchOver") {
      const winningTeam = model.summary?.winner ?? model.winner;
      return winningTeam === null
        ? "idle"
        : seat % 2 === winningTeam
          ? "win"
          : "lose";
    }
    if (model.out[seat]) return "out";
    if (
      (model.phase === "playing" || model.phase === "returnTribute") &&
      model.turn === seat
    )
      return "thinking";
    return avatarReactions[seat] ?? "idle";
  };

  const { ref: surfaceRef, width, height } = useSize();
  const [selected, setSelected] = useState<Card[]>([]);
  const [hintIndex, setHintIndex] = useState(0);
  const [reactionPickerOpen, setReactionPickerOpen] = useState(false);
  const [reactionPosition, setReactionPosition] = useState(initialReactionPosition);
  const reactionDrag = useRef<{
    pointerId: number; startX: number; startY: number; originX: number; originY: number; moved: boolean;
  } | null>(null);
  const [muted, setMuted] = useState(() => audio.settings.muted);
  const [activityOpen, setActivityOpen] = useState(false);
  const reactionWidth = REACTION_FAB_SIZE;
  const [tickerIndex, setTickerIndex] = useState(0);
  const tickerEntries = useMemo(
    () => model.activity.slice(-12),
    [model.activity],
  );

  useEffect(() => {
    setTickerIndex(Math.max(0, tickerEntries.length - 1));
  }, [tickerEntries.length, tickerEntries.at(-1)?.seq]);

  useEffect(() => {
    if (tickerEntries.length < 2 || activityOpen) return;
    const timer = window.setInterval(() => {
      setTickerIndex((current) => (current + 1) % tickerEntries.length);
    }, 2800);
    return () => window.clearInterval(timer);
  }, [tickerEntries.length, activityOpen]);

  useEffect(() => {
    if (!activityOpen) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setActivityOpen(false);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [activityOpen]);

  useEffect(() => {
    if (!reactionPickerOpen) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") setReactionPickerOpen(false);
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [reactionPickerOpen]);

  useEffect(() => {
    const clamp = () => setReactionPosition((current) => {
      const frame = frameRect();
      return {
        x: Math.max(frame.left + 10, Math.min(frame.right - reactionWidth - 10, current.x)),
        y: Math.max(frame.top + 64, Math.min(frame.bottom - REACTION_FAB_SIZE - 82, current.y)),
      };
    });
    // Once on mount as well as on resize: the initial position is computed by a
    // lazy `useState` initialiser, which runs before the frame is in the DOM
    // and so has only the window to measure against.
    clamp();
    window.addEventListener("resize", clamp);
    return () => window.removeEventListener("resize", clamp);
  }, [reactionWidth]);

  useEffect(() => {
    if (!wideTable) return;
    const id = window.requestAnimationFrame(() => {
      const frame = frameRect();
      setReactionPosition({
        x: frame.right - reactionWidth - 12,
        y: frame.top + 72,
      });
    });
    return () => window.cancelAnimationFrame(id);
  }, [height, reactionWidth, wideTable, width]);

  const rules = useMemo(
    () => rulesetById(room?.rulesetId ?? "standard"),
    [room?.rulesetId],
  );
  const measured = width > 0 && height > 0;
  const table = useMemo(
    () => wideTable
      ? wideLiveLayout(Math.max(1, width), Math.max(1, height), model.seat)
      : mobileLiveLayout(Math.max(1, width), Math.max(1, height), model.seat),
    [width, height, model.seat, wideTable],
  );
  const wideLiveTable = wideTable ? (table as WideLiveLayout) : null;
  const viewerCardWidth = wideLiveTable?.viewerCardWidth ?? table.cardWidth;
  const viewerCardHeight = wideLiveTable?.viewerCardHeight ?? table.cardHeight;
  const viewerFan = wideLiveTable?.viewerFan ?? table.fan;

  // A room, hand, or turn change invalidates the selection and helper tray.
  useEffect(() => {
    setSelected([]);
    setHintIndex(0);
  }, [roomId, model.turnToken, model.handNumber, model.hand]);

  // Keep the cue handler's view of the geometry current.
  useEffect(() => {
    geometryRef.current = {
      seats: [0, 1, 2, 3].map((seat) => { const s = table.seats.find(s => s.seat === seat)!; return { x: s.x, y: s.y, playX: s.playX, playY: s.playY }; }),
      cx: table.trickX,
      cy: table.trickY,
      w: table.width,
    };
  }, [table]);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => effects.setEnabled(!reduced.matches && !preferences.reducedMotion);
    sync();
    reduced.addEventListener("change", sync);
    return () => {
      reduced.removeEventListener("change", sync);
    };
  }, [effects, preferences.reducedMotion]);
  useEffect(() => () => effects.detach(), [effects]);

  useEffect(() => {
    effects.resize();
  }, [effects, width, height]);

  // Dev-only handle for firing effects by hand.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    (window as unknown as { __fx?: unknown }).__fx = {
      bomb: (power = 60) => effects.bomb(table.trickX, table.trickY, power),
      confetti: () => effects.confetti(table.width),
      coins: () => effects.coins(table.trickX, table.trickY),
      audio,
    };
  }, [effects, table]);

  // Browsers will not start an AudioContext without a gesture.
  useEffect(() => {
    const start = () => audio.unlock();
    window.addEventListener("pointerdown", start, { once: true });
    window.addEventListener("keydown", start, { once: true });
    return () => {
      window.removeEventListener("pointerdown", start);
      window.removeEventListener("keydown", start);
    };
  }, []);

  const trick = model.open ? null : (model.current?.combo ?? null);
  // Memoised on identity, because everything downstream of it is: rebuilding
  // the object every render invalidated the two memos below, and one of those
  // enumerates every legal play in a twenty-seven card hand.
  const trickCombo: Combo | null = useMemo(
    () => (trick === null ? null : { ...trick, cards: [...trick.cards] }),
    [trick],
  );

  const tributePhase = model.phase === "returnTribute";
  const myTurn = canAct && isOnTurn(model);
  const playableCombos = useMemo(
    () => myTurn && !tributePhase
      ? legalPlays(model.hand, trickCombo, model.level, rules)
      : [],
    [model.hand, model.level, myTurn, rules, tributePhase, trickCombo],
  );
  const hasPlayableHint = useMemo(() => {
    if (!myTurn) return false;
    if (tributePhase) return model.hand.length > 0;
    return playableCombos.length > 0;
  }, [model.hand.length, myTurn, playableCombos.length, tributePhase]);
  const activeHint = session.hints[hintIndex] ?? null;
  const hint = activeHint?.action.type === "play"
    ? activeHint.action.cards
    : activeHint?.action.type === "returnTribute"
      ? [activeHint.action.card]
      : [];
  const hintReason = (reason: HintReason | undefined): string | null => {
    switch (reason) {
      case "go-out": return t("hintGoOut");
      case "help-partner": return t("hintHelpPartner");
      case "preserve-bomb": return t("hintPreserveBomb");
      case "take-control": return t("hintTakeControl");
      case "shed-cards": return t("hintShedCards");
      case "low-cost": return t("hintLowCost");
      case "pass-partner": return t("hintPassPartner");
      case "save-strength": return t("hintSaveStrength");
      default: return null;
    }
  };

  useEffect(() => {
    const suggestion = session.hints[hintIndex];
    if (suggestion === undefined) return;
    if (suggestion.action.type === "play") setSelected([...suggestion.action.cards]);
    else if (suggestion.action.type === "returnTribute") setSelected([suggestion.action.card]);
    else setSelected([]);
  }, [hintIndex, session.hints]);

  /** Reading of the selected cards, and why it is not legal if it is not. */
  const verdict = useMemo(() => {
    if (selected.length === 0) return null;
    if (tributePhase) {
      const card = selected[0] as Card;
      if (selected.length > 1)
        return { ok: false, why: "Return exactly one card." };
      if (!isLegalReturnTributeCard(model.hand, card, rules.maxReturnTributeRank)) {
        return {
          ok: false,
          why: `A returned card has to be a ${rankLabel(rules.maxReturnTributeRank)} or lower.`,
        };
      }
      return { ok: true, why: `Return ${rankLabel(rankOf(card))}` };
    }
    const beats = canBeat(selected, trickCombo, model.level, rules);
    if (beats !== null) return { ok: true, why: comboLabel(beats) };
    return {
      ok: false,
      why: explain(selected, trickCombo, model.level, rules),
    };
  }, [selected, trickCombo, model.hand, model.level, rules, tributePhase]);

  const quickSelectionOptions = useMemo(
    () => myTurn && !tributePhase
      ? selectionOptions(model.hand, selected, trickCombo, model.level, rules, 4, playableCombos)
      : [],
    [model.hand, model.level, myTurn, playableCombos, rules, selected, tributePhase, trickCombo],
  );

  const toggle = useCallback(
    (card: Card) => {
      if (!myTurn) return;
      audio.unlock();
      clearHint();
      setSelected((current) => {
        const on = current.includes(card);
        audio.play(on ? "deselect" : "select");
        return on ? current.filter((c) => c !== card) : [...current, card];
      });
    },
    [myTurn, clearHint],
  );

  /**
   * The cards in a full phone hand overlap by design, but the visible strip of
   * each card is too narrow to be a reliable touch target. Keep the individual
   * buttons for keyboard and screen-reader access, preserve the browser's
   * topmost painted-card hit, and use shared fan geometry only as a fallback.
   */
  const selectFromFan = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!myTurn || event.button !== 0) return;
      const bounds = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - bounds.left;
      const y = event.clientY - bounds.top;
      const selectedIndexes = new Set<number>();
      model.hand.forEach((card, index) => { if (selected.includes(card)) selectedIndexes.add(index); });
      const target = event.target instanceof Element
        ? event.target.closest<HTMLElement>("[data-hand-index]")
        : null;
      const renderedTargetIndex = target !== null && event.currentTarget.contains(target)
        ? Number(target.dataset.handIndex)
        : null;
      const nearest = fanCardAtPoint({
        x,
        y,
        count: model.hand.length,
        fan: viewerFan,
        cardWidth: viewerCardWidth,
        cardHeight: viewerCardHeight,
        selected: selectedIndexes,
        renderedTargetIndex,
      });
      if (nearest === null) return;
      event.preventDefault();
      const card = model.hand[nearest];
      if (card !== undefined) toggle(card);
    },
    [model.hand, myTurn, selected, toggle, viewerCardHeight, viewerCardWidth, viewerFan],
  );

  const play = () => {
    if (verdict?.ok !== true) {
      audio.play("invalid");
      return;
    }
    if (tributePhase)
      session.act({ type: "returnTribute", card: selected[0] as Card });
    else session.act({ type: "play", cards: selected });
    setSelected([]);
  };

  const showHint = () => {
    audio.play("button");
    if (session.hints.length > 0) {
      setHintIndex((current) => (current + 1) % session.hints.length);
      return;
    }
    setHintIndex(0);
    session.requestHint();
  };

  const toggleReactionPicker = () => {
    audio.unlock();
    setReactionPickerOpen((open) => !open);
  };

  const startReactionDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (session.status !== "open") return;
    event.currentTarget.setPointerCapture(event.pointerId);
    reactionDrag.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      originX: reactionPosition.x,
      originY: reactionPosition.y,
      moved: false,
    };
  };

  const moveReactionDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = reactionDrag.current;
    if (drag === null || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (Math.hypot(dx, dy) > 5) drag.moved = true;
    if (drag.moved) {
      event.preventDefault();
      setReactionPickerOpen(false);
      setReactionPosition(clampReactionPosition({ x: drag.originX + dx, y: drag.originY + dy }));
    }
  };

  const finishReactionDrag = (event: React.PointerEvent<HTMLButtonElement>) => {
    const drag = reactionDrag.current;
    if (drag === null || drag.pointerId !== event.pointerId) return;
    reactionDrag.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    if (drag.moved) window.localStorage.setItem(REACTION_POSITION_KEY, JSON.stringify(reactionPosition));
    else toggleReactionPicker();
  };

  const leaveTable = async () => {
    if (model.seat === null) {
      navigate("/watch", { replace: true });
      return;
    }
    const confirmed = window.confirm(
      t("language") === "语言"
        ? "暂离牌桌后，系统会立即为你托管。无人参与时牌局会暂停，并在 5 分钟后中止，确认暂离吗？"
        : "Leave temporarily? The system will play for you while others are active. Unattended games pause and expire after 5 minutes.",
    );
    if (!confirmed) return;
    await api.setPresence(roomId, "away").catch(() => null);
    await refreshSession();
    navigate("/", { replace: true });
  };

  if (session.status === "gone") {
    return (
      <div style={{ "--scene-ms": `${drama?.durationMs ?? 220}ms` } as React.CSSProperties}
      className={`app-frame${wideTable ? " app-frame-wide" : ""} table-boot`}>
        <p>{t("tableFinished")}</p>
        <Button
          variant="primary"
          onClick={() => navigate("/", { replace: true })}
        >
          Back to the lobby
        </Button>
      </div>
    );
  }
  if (room === null)
    return (
      <div className={`app-frame${wideTable ? " app-frame-wide" : ""} table-boot`}>
        <Spinner label="Joining the table…" />
      </div>
    );

  if (room.lifecycle === "PAUSED" || room.lifecycle === "ABORTED") {
    const paused = room.lifecycle === "PAUSED";
    const chinese = t("language") === "语言";
    return <div className="app-frame table-boot">
      <h2>{paused ? (chinese ? "牌局已暂停" : "Game paused") : (chinese ? "牌局已中止" : "Game ended")}</h2>
      <p>{paused
        ? (chinese ? "暂时没有真人参与，已停止托管推进。请在恢复期限前继续游戏。" : "Play has paused because no players are active. Resume before the deadline.")
        : (chinese ? "恢复期限已过。已完成小局的结算会保留。" : "The resume deadline passed. Completed hand rewards are preserved.")}</p>
      {paused && room.resumeUntil && <p>{new Date(room.resumeUntil).toLocaleTimeString()}</p>}
      {paused && model.seat !== null && <Button variant="primary" onClick={() => void api.setPresence(roomId, "active").catch(() => null)}>
        {chinese ? "继续游戏" : "Resume game"}
      </Button>}
      <Button onClick={() => navigate("/", { replace: true })}>{t("backLobby")}</Button>
    </div>;
  }

  const seatInfo = (seat: Seat) => room.seats.find((s) => s.seat === seat);
  const visibleTrick =
    model.trickActions.length > 0 ? model.trickActions : model.lastTrickActions;
  const latestBySeat = [...visibleTrick].reduce<Map<Seat, TableAction>>(
    (actions, action) => {
      actions.set(action.seat, action);
      return actions;
    },
    new Map(),
  );
  const showingPrevious =
    model.trickActions.length === 0 && model.lastTrickActions.length > 0;
  const tickerEntry =
    tickerEntries[tickerIndex] ?? tickerEntries.at(-1) ?? null;

  return (
    <div
      className={`app-frame${wideTable ? " app-frame-wide table-wide" : ""} table${drama === null ? "" : ` table-scene-${drama.scene}${drama.intensity === "climax" ? " table-drama" : ""}`}`}
    >
      {model.seat !== null && room.seats.find((s) => s.seat === model.seat)?.autoPlaying &&
        <Button onClick={() => void api.setPresence(roomId, "active").catch(() => null)}>{t("language") === "语言" ? "取消托管" : "Take control"}</Button>}
      <header className="table-bar">
        <button
          className="table-leave"
          onClick={() => void leaveTable()}
          aria-label={t("backLobby")}
        >
          <ArrowLeft aria-hidden="true" />
        </button>
        <span className="table-level">
          {t("level")} <strong>{rankLabel(model.level)}</strong>
        </span>
        <span className="table-scores">
          <span className="team-chip team-a">{rankLabel(model.levels[0])}</span>
          <span className="team-chip team-b">{rankLabel(model.levels[1])}</span>
        </span>
        {session.status !== "open" && (
          <span className="table-status">
            {session.status === "connecting" ? t("catchingUp") : t("waiting")}
          </span>
        )}
        <button
          className="table-mute"
          onClick={() => {
            audio.unlock();
            setMuted(audio.update({ muted: !muted }).muted);
          }}
          aria-label={muted ? "Unmute" : "Mute"}
          aria-pressed={muted}
        >
          {muted ? <VolumeX aria-hidden="true" /> : <Volume2 aria-hidden="true" />}
        </button>
      </header>

      {import.meta.env.DEV && roomId === "presentation-qa" && <nav className="presentation-qa-controls" aria-label="Presentation QA">
        {["ordinary", "bomb", "straightFlush", "counter", "humbled", "jokerBomb", "firstOut", "jiefeng", "win", "lose"].map(scene => <a key={scene} href={`?scene=${scene}`}>{scene}</a>)}
      </nav>}
      <div className={`table-columns-shell${wideTable ? " wide-table-columns" : ""}`}>
      {wideTable && (
        <WideHistoryPanel
          actions={model.activity}
          seats={room.seats}
          level={model.level}
          onOpen={() => setActivityOpen(true)}
        />
      )}
      <div className="table-surface" ref={surfaceRef}>
        <div className="table-shake" ref={shakeRef}>
          {/* Seats. Nothing is positioned until the surface has a real size —
            placing cards against a fallback of 1x1 puts the whole table in the
            top-left corner for a frame. */}
          {measured && (
            <SeatRing
              table={table}
              entryFor={(seat) => {
                const info = seatInfo(seat);
                const box = table.seats.find(
                  (candidate) => candidate.seat === seat,
                );
                const isTurn =
                  model.turn === seat &&
                  (model.phase === "playing" ||
                    model.phase === "returnTribute");
                const expression = avatarExpression(seat);
                return {
                  name: info?.name ?? `Seat ${seat + 1}`,
                  avatarId: info?.avatarId ?? "avatar-01",
                  expression,
                  connection: info?.autoPlaying
                    ? "autoPlaying"
                    : info?.connected === false
                      ? "offline"
                      : undefined,
                  onTurn: isTurn,
                  badge: model.out[seat]
                    ? <span className={drama?.scene === "firstOut" && drama.seat === seat ? "first-out-badge" : ""}>{finishLabel(model.finished, seat)}</span>
                    : model.counts[seat],
                  note: drama?.scene === "jiefeng" && drama.seat === seat ? <span className="seat-pass">{t("teammateLead")}</span> : model.passed[seat] ? (
                    <span className="seat-pass">{t("pass")}</span>
                  ) : undefined,
                  overlay:
                    isTurn && model.deadlineAt !== null ? (
                      <TurnClock
                        deadlineAt={model.deadlineAt}
                        serverNow={session.serverNow}
                        size={(box?.avatarSize ?? 44) + 8}
                      />
                    ) : undefined,
                };
              }}
            />
          )}



          {/* Public cards remain next to the player who laid them for the whole
            trick. A player may act more than once; their latest action replaces
            only their own prior marker, while the full sequence stays in the log. */}
          {measured &&
            table.seats.map((box) => {
              const action = latestBySeat.get(box.seat);
              if (action === undefined) return null;
              const currentWinner =
                action.kind === "played" &&
                model.current?.seat === box.seat &&
                model.current.combo.cards.join(",") ===
                  action.combo?.cards.join(",");
              return (
                <div
                  key={`public-action-${action.seq}`}
                  className={`seat-play${action.seq === animatedPlaySeq ? " seat-play-animated" : ""}${action.kind === "passed" ? " seat-play-is-pass" : ""}${currentWinner ? " seat-play-leading" : ""}${drama?.seat === box.seat ? ` scene-${drama.scene}` : ""}${drama?.scene === "counter" && drama.relatedSeat === box.seat ? " scene-overthrown" : ""}`}
                  style={{ left: box.playX, top: box.playY, "--flight-x": `${(box.seat === model.seat ? viewerFan.centreX : box.x) - box.playX}px`, "--flight-y": `${(box.seat === model.seat ? viewerFan.baseY : box.y) - box.playY}px`, "--scene-ms": `${drama?.durationMs ?? 220}ms` } as React.CSSProperties}
                >
                  {action.kind === "passed" ? (
                    <span className="seat-play-pass">{t("pass")}</span>
                  ) : (
                    <ActionCards
                      action={action}
                      level={model.level}
                      width={Math.max(
                        table.mode === "portrait" ? 34 : 22,
                        table.trickCardWidth * (table.mode === "portrait" ? 0.9 : 0.92),
                      )}
                      height={Math.max(
                        table.mode === "portrait" ? 48 : 31,
                        table.trickCardHeight * (table.mode === "portrait" ? 0.9 : 0.92),
                      )}
                    />
                  )}
                  <span className="seat-play-label">
                    {action.kind === "played"
                      ? comboTypeName(action.combo?.type, t)
                      : t("passedAction")}
                  </span>
                </div>
              );
            })}

          {measured && visibleTrick.length === 0 && (
            <div
              className="trick"
              style={{ left: table.trickX, top: table.trickY }}
            >
              <span className="trick-empty">
                {model.open && myTurn ? t("yourLead") : ""}
              </span>
            </div>
          )}

        </div>
        <div className="table-hand-stable">
          {/* Own hand. */}
          <div
            className="fan"
            aria-label={t("yourHand")}
            onPointerDown={selectFromFan}
          >
            {measured &&
              model.hand.map((card, index) => {
                const place = fanPlacement(
                  index,
                  model.hand.length,
                  viewerFan,
                  viewerCardWidth,
                );
                const isSelected = selected.includes(card);
                return (
                  <CardFace
                    key={card}
                    card={card}
                    level={model.level}
                    width={viewerCardWidth}
                    height={viewerCardHeight}
                    selected={isSelected}
                    hinted={hint.includes(card)}
                    dimmed={
                      myTurn &&
                      selected.length > 0 &&
                      !isSelected &&
                      verdict?.ok === false
                    }
                    onClick={myTurn ? () => toggle(card) : undefined}
                    pointerActivation="parent"
                    handIndex={index}
                    style={{
                      transform:
                        `translate(${place.x - viewerCardWidth / 2}px, ` +
                        `${place.y - viewerCardHeight / 2 - (isSelected ? viewerFan.lift : 0)}px) ` +
                        `rotate(${place.rotation}deg)`,
                      zIndex: index,
                    }}
                  />
                );
              })}
          </div>
          {myTurn && !tributePhase && (selected.length > 0 || session.hints.length > 0) && (
            <SelectionAssist
              style={{
                left: viewerFan.centreX,
                top: Math.max(52, viewerFan.baseY - (viewerFan.rowSize && model.hand.length > viewerFan.rowSize ? viewerFan.rowGap ?? 0 : 0) - viewerCardHeight / 2 - viewerFan.lift - 10),
              }}
              manualOptions={session.hints.length === 0 ? quickSelectionOptions : []}
              hints={session.hints}
              hintIndex={hintIndex}
              hintLabel={(suggestion) => hintReason(suggestion.reasonKey)
                ?? (suggestion.action.type === "pass" ? t("hintNoPlay") : t("hint"))}
              onManualSelect={(option) => {
                audio.unlock();
                audio.play(option.active ? "button" : "select");
                clearHint();
                setSelected([...option.cards]);
              }}
              onHintSelect={setHintIndex}
            />
          )}
        </div>
        {measured && (
          <FloatingReactions table={table} reactions={tableReactions} />
        )}
        {!wideTable && <button
          className="activity-ticker"
          type="button"
          onClick={() => setActivityOpen(true)}
          aria-haspopup="dialog"
          aria-label={`${t("playHistory")}. ${t("activityHint")}`}
        >
          <span className="activity-ticker-kicker">
            {showingPrevious
              ? t("previousTrick")
              : t("trickNumber", {
                  count: tickerEntry?.trick ?? model.trickNumber,
                })}
          </span>
          {tickerEntry === null ? (
            <span className="activity-ticker-empty">{t("noActivity")}</span>
          ) : wideTable ? (
            <span className="activity-ticker-list">
              {tickerEntries.map((entry) => (
                <span className="activity-ticker-entry" key={entry.seq}>
                  <Avatar avatarId={seatInfo(entry.seat)?.avatarId ?? "avatar-01"} expression="idle" size={28} />
                  <strong>{seatInfo(entry.seat)?.name ?? `Seat ${entry.seat + 1}`}</strong>
                  <span>{entry.kind === "passed" ? t("passedAction") : comboTypeName(entry.combo?.type, t)}</span>
                  {entry.combo !== null && <TickerCards action={entry} level={model.level} />}
                </span>
              ))}
            </span>
          ) : (
            <span className="activity-ticker-entry" key={tickerEntry.seq}>
              <Avatar
                avatarId={seatInfo(tickerEntry.seat)?.avatarId ?? "avatar-01"}
                expression="idle"
                size={28}
              />
              <strong>
                {seatInfo(tickerEntry.seat)?.name ??
                  `Seat ${tickerEntry.seat + 1}`}
              </strong>
              <span>
                {tickerEntry.kind === "passed"
                  ? t("passedAction")
                  : comboTypeName(tickerEntry.combo?.type, t)}
              </span>
              {tickerEntry.combo !== null && (
                <TickerCards action={tickerEntry} level={model.level} />
              )}
            </span>
          )}
          <span className="activity-ticker-more" aria-hidden="true">
            <ChevronRight size={20} aria-hidden="true" />
          </span>
        </button>}
        <canvas className="table-fx" ref={fxRef} aria-hidden="true" />
        {speechBubble !== null && measured && (() => {
          const box = table.seats.find(s => s.seat === speechBubble.seat)!;
          const speechLeft = box.x < table.width / 2 + 1 ? box.x + box.avatarSize / 2 + 8 : box.x - box.avatarSize / 2 - 148;
          return <div className="table-speech" role="status" style={{ left: Math.max(8, Math.min(table.width - 148, speechLeft)), top: box.y - 16 }}>{speechBubble.text}</div>;
        })()}
        {drama?.scene === "jiefeng" && measured && (() => {
          const from = table.seats.find(s => s.seat === drama.relatedSeat)!;
          const to = table.seats.find(s => s.seat === drama.seat)!;
          return <svg className="table-team-link" aria-hidden="true"><line x1={from.x} y1={from.y} x2={to.x} y2={to.y} /></svg>;
        })()}
        {drama !== null && drama.intensity === "climax" && drama.scene !== "matchEnd" && (
          <div className="table-drama-banner" key={drama.id} style={{ "--scene-ms": `${drama.durationMs}ms` } as React.CSSProperties} aria-live="polite">
            <strong>{drama.scene === "jokerBomb" ? t("comboJokerBomb") : t("counterPlay")}</strong>
          </div>
        )}
      </div>
      {wideTable && (
        <WidePlayerPanel seats={room.seats} completedHands={completedHands} />
      )}
      </div>

      {model.seat !== null && (
        <div
          className={`reaction-floating${reactionPosition.y < frameRect().top + frameRect().height / 2 ? " reaction-floating-top" : ""}`}
          style={{ left: reactionPosition.x, top: reactionPosition.y }}
        >
          <button
            className="reaction-trigger"
            type="button"
            onPointerDown={startReactionDrag}
            onPointerMove={moveReactionDrag}
            onPointerUp={finishReactionDrag}
            onPointerCancel={() => { reactionDrag.current = null; }}
            onClick={(event) => { if (event.detail === 0) toggleReactionPicker(); }}
            disabled={session.status !== "open"}
            aria-label={t("reactions")}
            aria-expanded={reactionPickerOpen}
            aria-haspopup="dialog"
            title={t("reactions")}
          >
            <Smile size={24} aria-hidden="true" />
          </button>
          {reactionPickerOpen && (
            <section className="reaction-picker" role="dialog" aria-label={t("reactionPicker")}>
              <div className="reaction-grid">
                {TABLE_REACTIONS.map((reaction) => (
                  <button
                    key={reaction.id}
                    type="button"
                    className="reaction-choice"
                    aria-label={t(reaction.labelKey)}
                    title={t(reaction.labelKey)}
                    onClick={() => {
                      audio.unlock();
                      audio.play("button");
                      session.sendReaction(reaction.id);
                      setReactionPickerOpen(false);
                    }}
                  >
                    <img src={reaction.asset} alt="" draggable={false} />
                    <span className="reaction-choice-label">{t(reaction.labelKey)}</span>
                  </button>
                ))}
              </div>
            </section>
          )}
        </div>
      )}

      <footer className="table-actions" data-hero-seat={model.seat ?? undefined}>
        {preferences.coaching && (
          <Coach
            verdict={verdict}
            rejection={session.rejection}
            myTurn={myTurn}
            tributePhase={tributePhase}
            open={model.open}
            phase={model.phase}
            lag={session.lag}
            actionState={actionState}
          />
        )}
        <div className="action-row">
          <Button
            variant="ghost"
            className={`hint-button ${hasPlayableHint ? "hint-button-available" : "hint-button-empty"}`}
            onClick={showHint}
            disabled={!myTurn}
            busy={session.hintPending}
          >
            <Lightbulb size={20} aria-hidden="true" />{t("hint")}
          </Button>
          <Button
            variant="secondary"
            className={activeHint?.action.type === "pass" ? "pass-button-recommended" : undefined}
            onClick={() => session.act({ type: "pass" })}
            disabled={!myTurn || model.open || tributePhase}
          >
            <SkipForward size={20} aria-hidden="true" />{t("pass")}
          </Button>
          <Button
            variant="primary"
            onClick={play}
            disabled={!myTurn || verdict?.ok !== true}
          >
            <Send size={20} aria-hidden="true" />{tributePhase ? t("return") : t("play")}
          </Button>
        </div>
      </footer>

      {visibleHandRewards.length > 0 && <details className="table-hand-rewards">
        <summary>{t("handRewardHistory")} · +{visibleHandRewards.reduce((sum, h) => sum + h.receipt.xp.total, 0)} XP</summary>
        <HandRewardHistory hands={visibleHandRewards} />
      </details>}
      {model.phase === "handOver" && model.lastResult !== null && (
        <HandSummary result={model.lastResult} room={room} seat={model.seat}
          receipt={visibleHandRewards.find((hand) => hand.handNumber === model.handNumber)?.receipt} />
      )}
      {model.phase === "matchOver" && (
        <MatchEndTransition
          winner={model.summary?.winner ?? model.winner}
          celebrate={drama?.scene === "matchEnd"}
          room={room}
          seat={model.seat}
        />
      )}
      {activityOpen && (
        <ActivityDialog
          actions={model.activity}
          level={model.level}
          seatInfo={seatInfo}
          onClose={() => setActivityOpen(false)}
        />
      )}
    </div>
  );
}

function SelectionAssist({
  style,
  manualOptions,
  hints,
  hintIndex,
  hintLabel,
  onManualSelect,
  onHintSelect,
}: {
  style: React.CSSProperties;
  manualOptions: readonly SelectionOption[];
  hints: readonly HintSuggestion[];
  hintIndex: number;
  hintLabel: (suggestion: HintSuggestion) => string;
  onManualSelect: (option: SelectionOption) => void;
  onHintSelect: (index: number) => void;
}) {
  const { t } = useLocale();
  const showingHints = hints.length > 0;
  return (
    <section className="selection-assist" style={style} aria-label={showingHints ? t("hintOptions") : t("selectionOptions")}>
      <span className="selection-assist-kicker">{showingHints ? t("hint") : t("selectionOptions")}</span>
      <div className="selection-assist-scroll">
        {showingHints ? hints.slice(0, 3).map((suggestion, index) => {
          const cards = suggestion.action.type === "play"
            ? suggestion.action.cards
            : suggestion.action.type === "returnTribute"
              ? [suggestion.action.card]
              : [];
          const title = suggestion.action.type === "pass"
            ? t("pass")
            : comboTypeName(suggestion.combo?.type, t);
          return (
            <button
              type="button"
              className={`selection-option${index === hintIndex ? " selection-option-active" : ""}`}
              key={`${suggestion.reasonKey}-${index}`}
              aria-pressed={index === hintIndex}
              onClick={() => onHintSelect(index)}
            >
              <span className="selection-option-copy"><strong>{title}</strong><small>{hintLabel(suggestion)}</small></span>
              <SelectionOptionCards cards={cards} />
            </button>
          );
        }) : manualOptions.length > 0 ? manualOptions.map((option) => (
          <button
            type="button"
            className={`selection-option${option.active ? " selection-option-active" : ""}`}
            key={`${option.combo.type}:${option.combo.rank}:${option.cards.join(",")}`}
            aria-pressed={option.active}
            onClick={() => onManualSelect(option)}
          >
            <span className="selection-option-copy"><strong>{comboTypeName(option.combo.type, t)}</strong><small>{t("selectionChoose")}</small></span>
            <SelectionOptionCards cards={option.cards} />
          </button>
        )) : (
          <p className="selection-assist-empty">{t("selectionNoOptions")}</p>
        )}
      </div>
    </section>
  );
}

function SelectionOptionCards({ cards }: { cards: readonly Card[] }) {
  if (cards.length === 0) return null;
  return (
    <span
      className="selection-option-cards"
      role="img"
      aria-label={cards.map(cardLabel).join(" ")}
    >
      {cards.slice(0, 6).map((card) => <i key={card} aria-hidden="true">{cardLabel(card)}</i>)}
      {cards.length > 6 && <b aria-hidden="true">+{cards.length - 6}</b>}
    </span>
  );
}

function FloatingReactions({
  table,
  reactions,
}: {
  table: TableLayout;
  reactions: readonly ReactionEvent[];
}) {
  return (
    <div className="reaction-layer" aria-hidden="true">
      {reactions.map((reaction, index) => {
        const seat = table.seats.find((candidate) => candidate.seat === reaction.seat);
        if (seat === undefined) return null;
        const stack = reactions
          .slice(0, index)
          .filter((candidate) => candidate.seat === reaction.seat).length % 2;
        const definition = reactionDefinition(reaction.reactionId);
        return (
          <span
            key={reaction.id}
            className="reaction-sticker"
            style={{
              left: seat.x + (table.trickX - seat.x) * 0.17 + stack * 13,
              top: seat.y + (table.trickY - seat.y) * 0.17 - 16 - stack * 11,
            }}
          >
            <img src={definition.asset} alt="" draggable={false} />
          </span>
        );
      })}
    </div>
  );
}

export function MatchSettlement({
  summary,
  room,
  seat,
  onLobby, page = false, onRematch, onReplay, busy = false, error,
}: {
  page?: boolean; onRematch?: () => void; onReplay?: () => void; busy?: boolean; error?: string | null;
  summary: MatchSummary | null;
  room: { roomId: string; seats: readonly { seat: number; name: string; avatarId: string }[] };
  seat: Seat | null;
  onLobby: () => void;
}) {
  const { t, locale } = useLocale();
  const [shareState, setShareState] = useState<"idle" | "working" | "copied">(
    "idle",
  );
  const [shareNote, setShareNote] = useState<string | null>(null);
  const [activeMoment, setActiveMoment] = useState(0);
  if (summary === null) {
    return (
      <div className={page ? "settlement-page" : "settlement-backdrop"}>
        <section className="settlement-shell">
          <p>{t("noSummary")}</p>
          <Button variant="primary" onClick={onLobby}>
            {t("continueToLobby")}
          </Button>
        </section>
      </div>
    );
  }
  const won = summary.winner === (seat ?? 0) % 2;
  const nameOf = (playerSeat: Seat | null) =>
    playerSeat === null
      ? null
      : (room.seats.find((info) => info.seat === playerSeat)?.name ??
        `Seat ${playerSeat + 1}`);
  const labelFor = (label: PlayerMatchStats["labels"][number]) => t(label);
  const share = async () => {
    if (shareState === "working") return;
    setShareState("working");
    setShareNote(null);
    try {
      const result = await api.shareReplay(room.roomId);
      const url = new URL(result.url, window.location.origin).toString();
      try { await navigator.clipboard.writeText(url); setShareState("copied"); }
      catch { setShareNote(url); setShareState("idle"); }
    } catch {
      setShareNote(t("somethingWrong"));
      setShareState("idle");
    }
  };
  const moment = summary.moments[activeMoment] ?? null;
  return (
    <div className={page ? "settlement-page" : "settlement-backdrop"}>
      <section
        className={`settlement-shell settlement-${won ? "win" : "lose"}`}
        role={page ? "region" : "dialog"}
        aria-modal={page ? undefined : true}
        aria-labelledby="settlement-title"
      >
        <header className="settlement-hero">
          <div>
            <span className="settlement-kicker">{t("settlement")}</span>
            <h2 id="settlement-title">
              {won ? t("matchWonTitle") : t("matchLostTitle")}
            </h2>
            <p>
              {t("matchWinner", { team: summary.winner === 0 ? "A" : "B" })}
            </p>
          </div>
          <div className="settlement-trophy" aria-hidden="true">
            {won ? "✦" : "◇"}
          </div>
        </header>

        {summary.players.find((player) => player.seat === seat)?.handSettlements &&
          <HandRewardHistory hands={summary.players.find((player) => player.seat === seat)!.handSettlements!} />}
        <div className="settlement-grid">
          <section className="settlement-module settlement-stats">
            <h3>{t("matchStats")}</h3>
            <div className="settlement-stat-grid">
              <SettlementStat
                label={t("handsPlayed")}
                value={summary.handsPlayed}
              />
              <SettlementStat
                label={t("duration")}
                value={formatDuration(summary.durationMs)}
              />
              <SettlementStat
                label={t("teamA")}
                value={
                  summary.hands.filter((hand) => hand.winningTeam === 0).length
                }
              />
              <SettlementStat
                label={t("teamB")}
                value={
                  summary.hands.filter((hand) => hand.winningTeam === 1).length
                }
              />
            </div>
            <div className="settlement-level-path">
              <span>{t("levelPath")}</span>
              <strong>
                {summary.hands
                  .map((hand) => rankLabel(hand.level))
                  .join(" → ") || rankLabel(summary.levels[0])}
              </strong>
            </div>
            <ol className="settlement-rounds">
              {summary.hands.map((hand) => (
                <li key={hand.handNumber}>
                  <span>{hand.handNumber}</span>
                  <b>{hand.winningTeam === 0 ? t("teamA") : t("teamB")}</b>
                  <em>+{hand.upgrade}</em>
                  <small>
                    {hand.finished
                      .map((playerSeat) => nameOf(playerSeat))
                      .join(" · ")}
                  </small>
                </li>
              ))}
            </ol>
          </section>

          <section className="settlement-module settlement-read">
            <h3>{t("gameRead")}</h3>
            <div className="settlement-moment-card" aria-live="polite">
              {moment === null ? (
                <p>{t("noSummary")}</p>
              ) : (
                <>
                  <span className="settlement-moment-index">
                    {activeMoment + 1} / {summary.moments.length}
                  </span>
                  <strong>{momentTitle(moment, nameOf, t)}</strong>
                  {moment.cards.length > 0 && (
                    <span
                      className="settlement-moment-cards"
                      aria-label={t("playedCards", {
                        count: moment.cards.length,
                      })}
                    >
                      {moment.cards.slice(0, 8).map((card) => (
                        <CardFace
                          key={card}
                          card={card}
                          level={
                            summary.hands.find(
                              (hand) => hand.handNumber === moment.handNumber,
                            )?.level ?? 2
                          }
                          width={30}
                          height={42}
                        />
                      ))}
                    </span>
                  )}
                  <small>
                    {t("trickNumber", { count: moment.handNumber })}
                  </small>
                </>
              )}
            </div>
            <div className="settlement-moment-actions">
              <button
                type="button"
                onClick={() =>
                  setActiveMoment((index) => Math.max(0, index - 1))
                }
                disabled={activeMoment === 0}
                aria-label={t("previousMoment")}
              >
                ‹
              </button>
              <div className="settlement-dots" aria-hidden="true">
                {summary.moments.slice(0, 8).map((item, index) => (
                  <i
                    key={`${item.kind}-${item.handNumber}-${index}`}
                    className={index === activeMoment ? "active" : ""}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={() =>
                  setActiveMoment((index) =>
                    Math.min(summary.moments.length - 1, index + 1),
                  )
                }
                disabled={activeMoment >= summary.moments.length - 1}
                aria-label={t("nextMoment")}
              >
                <ChevronRight size={20} aria-hidden="true" />
              </button>
            </div>
          </section>

          <section className="settlement-module settlement-players">
            <h3>{t("playerPerformance")}</h3>
            <div className="settlement-player-list">
              {summary.players.map((player) => {
                const info = room.seats.find(
                  (item) => item.seat === player.seat,
                );
                return (
                  <article
                    key={player.seat}
                    className={`settlement-player team-${player.team}`}
                  >
                    <Avatar
                      avatarId={info?.avatarId ?? "avatar-01"}
                      expression={
                        player.labels.includes("mvp") ? "win" : "idle"
                      }
                      size={48}
                    />
                    <div className="settlement-player-name">
                      <strong>
                        {info?.name ?? `${t("team")} ${player.seat + 1}`}
                      </strong>
                      <small>
                        {player.labels.map(labelFor).join(" · ") || t("team")}
                      </small>
                    </div>
                    <div className="settlement-player-metrics">
                      <span>
                        {t("firstOut")} <b>{player.firstOuts}</b>
                      </span>
                      <span>
                        {t("trickWins")} <b>{player.trickWins}</b>
                      </span>
                      <span>
                        {t("bombCount")} <b>{player.bombs}</b>
                      </span>
                      <span>
                        {t("passedCount")} <b>{player.passes}</b>
                      </span>
                    </div>
                    <div className="settlement-player-reward">
                      <span>
                        {t("coinsEarned")} <b>{player.reward.coins}</b>
                      </span>
                      <span>
                        {t("xpEarned")} <b>{player.reward.xp}</b>
                      </span>
                      <span>
                        {t("ratingChange")}{" "}
                        <b>
                          {player.reward.ratingDelta >= 0 ? "+" : ""}
                          {player.reward.ratingDelta}
                        </b>
                      </span>
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        </div>

        {shareNote && <p role="status" className="settlement-share-note">{shareNote}</p>}
        {error && <p role="alert" className="error-note">{error}</p>}
        <footer className="settlement-actions">
          {onRematch && <Button variant="primary" busy={busy} onClick={onRematch}>{locale === "zh-CN" ? "再来一局" : "Play again"}</Button>}
          {onReplay && <Button onClick={onReplay}>{t("replays")}</Button>}
          <Button
            variant="ghost"
            onClick={share}
            disabled={shareState === "working"}
          >
            {shareState === "copied" ? t("copiedReplay") : t("shareReplay")}
          </Button>
          <Button variant="primary" onClick={onLobby}>
            {t("continueToLobby")}
          </Button>
        </footer>
      </section>
    </div>
  );
}

function SettlementStat({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <div className="settlement-stat">
      <small>{label}</small>
      <strong>{value}</strong>
    </div>
  );
}

function formatDuration(durationMs: number): string {
  const seconds = Math.max(0, Math.round(durationMs / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
}

function momentTitle(
  moment: MatchSummary["moments"][number],
  nameOf: (seat: Seat | null) => string | null,
  t: ReturnType<typeof useLocale>["t"],
): string {
  const name = nameOf(moment.seat) ?? t("team");
  if (moment.kind === "firstOut") return t("firstOutMoment", { name });
  if (moment.kind === "bomb")
    return t("bombMoment", {
      name,
      combo: comboTypeName(moment.comboType ?? "bomb", t),
    });
  if (moment.kind === "trickWin") return t("trickMoment", { name });
  return t("matchMoment", { team: moment.winningTeam === 0 ? "A" : "B" });
}

type Translate = ReturnType<typeof useLocale>["t"];

function comboTypeName(type: ComboType | undefined, t: Translate): string {
  const keys: Record<ComboType, Parameters<Translate>[0]> = {
    single: "comboSingle",
    pair: "comboPair",
    triple: "comboTriple",
    fullHouse: "comboFullHouse",
    straight: "comboStraight",
    consecutivePairs: "comboConsecutivePairs",
    consecutiveTriples: "comboConsecutiveTriples",
    straightFlush: "comboStraightFlush",
    bomb: "comboBomb",
    jokerBomb: "comboJokerBomb",
  };
  return type === undefined ? t("playedCards", { count: 0 }) : t(keys[type]);
}

function ActionCards({
  action,
  level,
  width,
  height,
}: {
  action: TableAction;
  level: number;
  width: number;
  height: number;
}) {
  const cards = action.combo?.cards ?? [];
  const spread = Math.max(7, width * 0.34);
  return (
    <span
      className="action-cards"
      style={{ width: width + Math.max(0, cards.length - 1) * spread, height }}
    >
      {cards.map((card, index) => (
        <CardFace
          key={card}
          card={card}
          level={level}
          width={width}
          height={height}
          style={{ left: index * spread, zIndex: index, "--card-index": index } as React.CSSProperties}
        />
      ))}
    </span>
  );
}

function TickerCards({
  action,
  level,
}: {
  action: TableAction;
  level: number;
}) {
  const cards = action.combo?.cards ?? [];
  const shown = cards.slice(0, 4);
  return (
    <span
      className="ticker-cards"
      aria-label={useLocale().t("playedCards", { count: cards.length })}
    >
      {shown.map((card) => (
        <CardFace key={card} card={card} level={level} width={20} height={28} />
      ))}
      {cards.length > shown.length && <b>+{cards.length - shown.length}</b>}
    </span>
  );
}

function ActivityDialog({
  actions,
  level,
  seatInfo,
  onClose,
}: {
  actions: readonly TableAction[];
  level: number;
  seatInfo: (seat: Seat) => { name: string; avatarId: string } | undefined;
  onClose: () => void;
}) {
  const { t } = useLocale();
  const listRef = useRef<HTMLOListElement | null>(null);
  const groups = groupTableActionsByTrick(actions);
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, []);
  return (
    <div
      className="activity-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="activity-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="activity-title"
      >
        <header>
          <div>
            <span>{t("currentTrick")}</span>
            <h2 id="activity-title">{t("playHistory")}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            autoFocus
            aria-label={t("close")}
          >
            ×
          </button>
        </header>
        {actions.length === 0 ? (
          <p className="activity-empty">{t("noActivity")}</p>
        ) : (
          <ol ref={listRef} className="activity-list">
            {groups.map((group) => (
              <li className="activity-trick-group" key={group.trick}>
                <div className="activity-trick-divider"><span>{t("trickNumber", { count: group.trick })}</span></div>
                <ol className="activity-trick-actions">
                  {group.actions.map((action) => (
                    <li
                      key={action.seq}
                      className={`activity-row activity-${action.kind}`}
                    >
                      <Avatar
                        avatarId={seatInfo(action.seat)?.avatarId ?? "avatar-01"}
                        expression="idle"
                        size={38}
                      />
                      <span className="activity-player">
                        <strong>
                          {seatInfo(action.seat)?.name ?? `Seat ${action.seat + 1}`}
                        </strong>
                        <small>
                          {action.kind === "passed"
                            ? t("passedAction")
                            : comboTypeName(action.combo?.type, t)}
                        </small>
                      </span>
                      {action.kind === "passed" ? (
                        <span className="activity-pass-mark">{t("pass")}</span>
                      ) : (
                        <ActionCards
                          action={action}
                          level={level}
                          width={30}
                          height={42}
                        />
                      )}
                    </li>
                  ))}
                </ol>
              </li>
            ))}
          </ol>
        )}
      </section>
    </div>
  );
}

/**
 * The rules coach.
 *
 * Explains *why* rather than shaking a red border, because most players at this
 * table will never have seen Guandan. Local reasoning comes first so the answer
 * is instant; a server rejection overrides it, since the server is right.
 */
function Coach({
  verdict,
  rejection,
  myTurn,
  tributePhase,
  open,
  phase,
  lag,
  actionState,
}: {
  verdict: { ok: boolean; why: string } | null;
  rejection: { message: string } | null;
  myTurn: boolean;
  tributePhase: boolean;
  open: boolean;
  phase: string;
  lag: number;
  actionState: ReturnType<typeof useGameSession>["actionState"];
}) {
  const { t } = useLocale();
  if (rejection !== null)
    return <p className="coach coach-bad">{rejection.message}</p>;
  if (phase === "handOver") return <p className="coach">{t("handOver")}</p>;
  if (actionState === "presenting" || actionState === "reconnecting")
    return <p className="coach coach-dim">{t("catchingUp")}</p>;
  if (lag > 6) return <p className="coach coach-dim">{t("catchingUp")}</p>;
  if (!myTurn) return <p className="coach coach-dim">{t("waiting")}</p>;
  if (tributePhase) {
    return <p className="coach">{t("oweCard")}</p>;
  }
  if (verdict === null) {
    return <p className="coach">{open ? t("yourLead") : t("beatOrPass")}</p>;
  }
  return (
    <p className={`coach ${verdict.ok ? "coach-good" : "coach-bad"}`}>
      {verdict.why}
    </p>
  );
}

function HandSummary({
  result,
  room,
  seat,
  receipt,
}: {
  result: NonNullable<import("@guandan/protocol").TableModel["lastResult"]>;
  receipt?: import("@guandan/protocol").SettlementReceipt;
  room: NonNullable<ReturnType<typeof useGameSession>["room"]>;
  seat: Seat | null;
}) {
  const { t } = useLocale();
  const order = [...result.finished, ...result.unfinished];
  const winners = room.seats.filter(
    (player) => player.seat % 2 === result.winningTeam,
  );
  const won = seat !== null && seat % 2 === result.winningTeam;
  return (
    <div className="hand-result-layer">
      <section
        className={`hand-result-card${won ? " hand-result-won" : ""}`}
        role="status"
        aria-live="polite"
      >
        <header>
          <div className="hand-result-winners">
            {winners.map((player) => (
              <Avatar
                key={player.userId}
                avatarId={player.avatarId}
                expression="win"
                size={50}
                title={player.name}
              />
            ))}
          </div>
          <div>
            <small>{t("handSettlement")}</small>
            <h2>
              {t("teamUpgraded", {
                team: result.winningTeam === 0 ? "A" : "B",
                count: result.upgrade,
              })}
            </h2>
            <p>
              {result.droppedToLevel !== null
                ? t("droppedLevel", { level: rankLabel(result.droppedToLevel) })
                : result.heldAtA
                  ? t("keptAtA")
                  : t("levelNow", { level: rankLabel(result.levelAfter) })}
            </p>
          </div>
          <strong className="hand-upgrade-badge">+{result.upgrade}</strong>
        </header>
        {seat !== null && <HandRewards receipt={receipt} />}
        <div className="hand-level-path" aria-label={t("levelPath")}>
          <b>{rankLabel(result.levelBefore)}</b>
          <span aria-hidden="true">→</span>
          <b>{rankLabel(result.levelAfter)}</b>
        </div>
        <div className="hand-finish-order">
          <span>{t("finishOrder")}</span>
          <ol>
            {order.map((playerSeat, index) => {
              const player = room.seats.find(
                (item) => item.seat === playerSeat,
              );
              return (
                <li key={playerSeat}>
                  <em>{index + 1}</em>
                  <Avatar
                    avatarId={player?.avatarId ?? "avatar-01"}
                    expression={index === 0 ? "win" : "idle"}
                    size={34}
                    title={player?.name ?? t("team")}
                  />
                  <small>
                    {player?.name ?? `${t("team")} ${playerSeat + 1}`}
                  </small>
                </li>
              );
            })}
          </ol>
        </div>
        <footer>
          <span>{t("nextHandSoon")}</span>
          <i aria-hidden="true" />
        </footer>
      </section>
    </div>
  );
}

function MatchEndTransition({ winner, room, seat, celebrate }: {
  winner: number | null;
  celebrate: boolean;
  room: NonNullable<ReturnType<typeof useGameSession>["room"]>;
  seat: Seat | null;
}) {
  const { t } = useLocale();
  const { preferences } = useUiPreferences();
  const won = winner !== null && seat !== null && seat % 2 === winner;
  const celebration = useMemo(() => new Effects(), []);
  const canvasRef = useCallback((node: HTMLCanvasElement | null) => {
    celebration.detach();
    if (node === null || !celebrate || !won || preferences.reducedMotion || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    celebration.attach(node); celebration.confetti(node.clientWidth);
  }, [celebration, celebrate, won, preferences.reducedMotion]);
  useEffect(() => () => celebration.detach(), [celebration]);
  const players = room.seats.filter(
    (player) => winner !== null && player.seat % 2 === winner,
  );
  return (
    <div className="match-end-layer" role="status" aria-live="polite">
      <canvas className="match-confetti" ref={canvasRef} aria-hidden="true" />
      <section className={won ? "match-end-card won" : "match-end-card"}>
        <div className="match-end-avatars">
          {players.map((player) => (
            <Avatar
              key={player.userId}
              avatarId={player.avatarId}
              expression="win"
              size={58}
              title={player.name}
            />
          ))}
          <span aria-hidden="true">✦</span>
        </div>
        <small>{t("settlement")}</small>
        <h2>{won ? t("matchWonTitle") : t("matchLostTitle")}</h2>
        <p>{t("matchSettling")}</p>
      </section>
    </div>
  );
}

function finishLabel(finished: readonly Seat[], seat: Seat): string {
  const place = finished.indexOf(seat);
  return place < 0 ? "out" : (["1st", "2nd", "3rd", "4th"][place] ?? "out");
}

/**
 * The turn clock.
 *
 * Rendered against the server's own deadline corrected by the measured clock
 * offset, never as a local countdown started when the turn arrived. A client
 * that was slow to receive the turn genuinely has less time, and showing it a
 * full clock would promise time the server will not honour.
 */
function TurnClock({
  deadlineAt,
  serverNow,
  size,
}: {
  deadlineAt: number;
  serverNow: () => number;
  size: number;
}) {
  const [remaining, setRemaining] = useState(() => deadlineAt - serverNow());

  useEffect(() => {
    const tick = () => setRemaining(deadlineAt - serverNow());
    tick();
    const id = window.setInterval(tick, 100);
    return () => window.clearInterval(id);
  }, [deadlineAt, serverNow]);

  const total = 25_000;
  const fraction = Math.max(0, Math.min(1, remaining / total));
  const seconds = Math.max(0, Math.ceil(remaining / 1000));
  const urgent = remaining <= 5_000;
  const r = size / 2 - 2;
  const circumference = 2 * Math.PI * r;

  return (
    <svg
      className={`turn-clock${urgent ? " turn-clock-urgent" : ""}`}
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="timer"
      aria-label={useLocale().t("secondsLeft", { count: seconds })}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke="rgb(0 0 0 / 45%)"
        strokeWidth="3"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        fill="none"
        stroke={urgent ? "var(--danger)" : "var(--gold)"}
        strokeWidth="3"
        strokeLinecap="round"
        strokeDasharray={`${circumference * fraction} ${circumference}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
    </svg>
  );
}

/** Why the selected cards cannot be played — in words, not codes. */
function explain(
  cards: readonly Card[],
  trick: Combo | null,
  level: number,
  rules: ReturnType<typeof rulesetById>,
): string {
  if (trick === null) {
    return cards.length === 1
      ? "That is a single — fine as a lead."
      : "Those cards do not make a play. Try a pair, a run of five, or a bomb.";
  }
  const shape = comboLabel(trick);
  const sameShape = canBeat(cards, null, level as never, rules);
  if (sameShape === null) return "Those cards do not make a play together.";
  if (sameShape.type === trick.type && sameShape.length === trick.length) {
    return `Your ${sameShape.type} is too low — it has to beat the ${shape} on the table.`;
  }
  return `A ${shape} can only be beaten by a higher one, or by a bomb.`;
}
